import { share, map } from 'rxjs/operators';
import { Injectable, OnInit, Predicate } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { MachineCategory, MachineModel, MachineProductLine } from '../models/index';
import * as _ from 'lodash';
import { ApiService } from './api.service';
import { ApiMode, ApplicationType } from '../../utils/enums';
import { OfflineApiService } from './offlineApi.service';
import { BaseService } from './baseService';
import { AppConstants } from '../../utils/appConstants.class';
import { PeopleService } from './people.service';
import { LogService } from './log.service';

@Injectable({
  providedIn: 'root'
})
export class MachineFamillyService extends BaseService {
  private modelsById: any = {};
  private productLinesById: any = {};
  private categoriesById: any = {};
  private productLineSubject = new BehaviorSubject<any>(null);
  private modelsLoader: Observable<any>;
  private productLinesLoader: Observable<any>;
  private categoriesLoader: Observable<any>;
  private baseUrl = '/plugins/machineselector/';

  constructor(
    private apiService: ApiService,
    private offlineApi: OfflineApiService,
    private logService: LogService,
    private peopleService: PeopleService
  ) {
    super();
    switch (this.applicationType) {
      case ApplicationType.Optimization:
        this.baseUrl = '/plugins/optmachineselector/';
        break;

      case ApplicationType.Installation:
        this.baseUrl = '/plugins/instmachineselector/';
        break;
    }
    this.loadModelsData();
    this.loadCategoriesData();
    this.loadProductLinesData();
  }

  public uploadEquipments(files: any): Observable<any> {
    const url = '/equipments/upload';
    return this.apiService.postFormData(url, files).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public getModels(): Observable<any> {
    if (this.modelsLoader) {
      return this.modelsLoader;
    }
    return new BehaviorSubject(_.values(this.modelsById)).asObservable();
  }

  // machines models
  loadModelsData() {
    const self = this;
    this.modelsLoader = this.loadAllModels().pipe(
      share(),
      map((res: MachineModel[]) => {
        this.modelsLoader = null;
        const models = res;

        _.map(models, (el) => {
          self.modelsById[el.Id] = new MachineModel(el.Id, el.CategoryId, el.Name, el.Serie, el.Hidden);
        });

        return _.values(this.modelsById);
      })
    );

    this.modelsLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Models load error')
    );
  }

  private loadAllModels() {
    const url = this.baseUrl + 'models';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.models);
  }

  public getModel(id: string): Observable<any> {
    return new BehaviorSubject(this.modelsById[id]).asObservable();
  }

  // machines product lines

  public getProductLines(): Observable<any> {
    if (this.productLinesLoader) {
      return this.productLinesLoader;
    }
    return new BehaviorSubject(_.values(this.productLinesById)).asObservable();
  }

  public getFilteredProductLines(predicate: Predicate<any>) {
    return this.getProductLines().map((productLines) => productLines.filter((p) => predicate(p)));
  }

  public getCurrentScopeProductLines(): Observable<any> {
    return this.peopleService.getCurrent().flatMap((current) => {
      if (current.productLine.includes('productline-*')) {
        return this.getProductLines();
      }
      return this.getFilteredProductLines(
        this.some([(producltLine) => current.productLine.includes(`productline-${producltLine.id}`)])
      );
    });
  }

  loadProductLinesData() {
    const self = this;
    this.productLinesLoader = this.loadAllProductLines().pipe(
      share(),
      map((res: MachineProductLine[]) => {
        this.productLinesLoader = null;
        const models = res;

        _.map(models, (el) => {
          self.productLinesById[el.Id] = new MachineProductLine(el.Id, el.Name);
        });
        this.productLineSubject.next(true);
        return _.values(this.productLinesById);
      })
    );
    this.productLinesLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Products load error')
    );
  }

  private loadAllProductLines() {
    const url = this.baseUrl + 'productlines';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.productLines);
  }

  public getProductLine(id: string): Observable<any> {
    return new BehaviorSubject(this.productLinesById[id]).asObservable();
  }

  public getProductLineById(id: string): Observable<any> {
    return this.productLineSubject.asObservable().pipe(map((res) => this.productLinesById[id]));
  }

  // machines famillies = categories
  public getCategories(): Observable<any> {
    if (this.categoriesLoader) {
      return this.categoriesLoader;
    }
    return new BehaviorSubject(_.values(this.categoriesById)).asObservable();
  }

  loadCategoriesData() {
    const self = this;
    this.categoriesLoader = this.loadAllCategories().pipe(
      share(),
      map((res: MachineCategory[]) => {
        this.categoriesLoader = null;
        const models = res;

        _.map(models, (el) => {
          self.categoriesById[el.Id] = new MachineCategory(el.Id, el.ProductLineId, el.Name);
        });

        return _.values(this.categoriesById);
      })
    );

    this.categoriesLoader.subscribe(
      (data: any) => {},
      (err: any) => this.logService.logError(err, 'Categories load error')
    );
  }

  private loadAllCategories() {
    const url = this.baseUrl + 'categories';
    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.categories);
  }

  public getCategory(id: string): Observable<any> {
    return new BehaviorSubject(this.categoriesById[id]).asObservable();
  }

  public getAllProductLineItem() {
    return new MachineModel('*', '', 'ALL', '');
  }
}
